/** @jsx isolateComponent */
import gql from 'graphql-tag';
import { isolateComponent } from '@zapier/style-encapsulation';
import { daysFromNow, parseDateTime } from '@zapier/date';
import { Price } from '@zapier/currency';
import { Alert } from '@zapier/design-system';

import type { Result } from '../useAlertBanner/alertBannerNotificationsQuery';
import type { ApolloCache } from '../types';
import type { BannerProps } from './types';

export const shouldRender = (data: Result['data']) => {
  return data.currentAccount.isTrialingTeams;
};

export const setTeamAutoUpgradeMutation = gql`
  mutation setTeamAutoUpgrade($accountId: Int!, $shouldAutoUpgrade: Boolean!) {
    setTeamAutoUpgrade(id: $accountId, shouldAutoUpgrade: $shouldAutoUpgrade) {
      id
      teamsTrialAutoUpgrade
    }
  }
`;

const getAutoUpgradeButtonText = ({
  error,
  loading,
  willAutoUpgrade,
}: {
  error?: string;
  loading?: boolean;
  willAutoUpgrade: boolean;
}) => {
  if (loading) {
    return willAutoUpgrade
      ? 'Disabling auto-upgrade…'
      : 'Enabling auto-upgrade…';
  }
  if (error) {
    return willAutoUpgrade
      ? 'Error disabling auto-upgrade…'
      : 'Error enabling auto-upgrade…';
  }
  return willAutoUpgrade ? 'Disable auto-upgrade' : 'Enable auto-upgrade';
};

export const Component = function TeamsTrialing(props: BannerProps) {
  const { data, currency } = props;
  const { currentAccount } = data;
  const willAutoUpgrade = currentAccount.teamsTrialAutoUpgrade;
  const canToggleAutoUpgrade = ['owner', 'admin'].includes(
    currentAccount.currentUserRole
  );
  const nextPlanAmount = data.nextTeamPlan?.amount ?? -1;
  const interval = currentAccount.plan.interval;
  const isYearly = interval === 'year';
  const monthlyAmount = isYearly ? nextPlanAmount / 12 : nextPlanAmount;
  const trialDaysRemaining = currentAccount.teamTrialEndDate
    ? daysFromNow(currentAccount.teamTrialEndDate)
    : 0;

  const defaultMutationReturnData = {
    id: currentAccount.id,
    teamsTrialAutoUpgrade: currentAccount.teamsTrialAutoUpgrade,
  };
  const [toggleTeamAutoUpgrade, { error, loading }] = props.useMutation(
    setTeamAutoUpgradeMutation,
    {
      update: (
        cache: ApolloCache,
        { data: respData = defaultMutationReturnData }
      ) => {
        cache.modify({
          id: cache.identify(respData),
          fields: {
            teamsTrialAutoUpgrade() {
              return respData.teamsTrialAutoUpgrade;
            },
          },
        });
      },
    }
  );

  const onToggleAutoUpgrade = () => {
    toggleTeamAutoUpgrade({
      variables: {
        accountId: currentAccount.id,
        shouldAutoUpgrade: !willAutoUpgrade,
      },
    });
  };

  const autoUpgradeToggle = canToggleAutoUpgrade && (
    <button
      type="button"
      css={{ textDecoration: 'underline' }}
      onClick={onToggleAutoUpgrade}
    >
      {getAutoUpgradeButtonText({ error, loading, willAutoUpgrade })}
    </button>
  );

  const daysText = trialDaysRemaining === 1 ? 'day' : 'days';
  const title = `${trialDaysRemaining} ${daysText} left in your Team trial`;
  const formattedEndDate = parseDateTime(currentAccount.teamTrialEndDate!)
    .toLocaleString(undefined, { month: 'short', day: 'numeric' })
    .replace(/([a-z]+)/i, '$1.');
  const intervalText = isYearly ? '(paid annually)' : '';
  const content = willAutoUpgrade ? (
    <p>
      {`On ${formattedEndDate}, you’ll be automatically upgraded and charged `}
      {monthlyAmount < 0 ? (
        ''
      ) : (
        <span>
          <Price currency={currency}>{monthlyAmount}</Price>/month{' '}
          {intervalText.trim()}
        </span>
      )}{' '}
      {autoUpgradeToggle}
    </p>
  ) : (
    <p>
      {`On ${formattedEndDate}, you’ll lose access to Team features. `}
      {autoUpgradeToggle} {'to stay on the Team plan when your trial ends'}
    </p>
  );

  return (
    <Alert layout={props.layout} title={title} type="info">
      {content}
    </Alert>
  );
};
